<template>
  <div>
    <!-- Top Container: Integration selection, date selectors, & games dropdown -->
    <div class="sm:p-8 p-4 bg-blue-100 rounded-md grid grid-cols-2 gap-4">
      <!-- Integration Dropdown -->
      <div>
        <label class="block font-medium text-md text-gray-700">Select Integration</label>
        <DropdownComponent
          :items="newIntegrations"
          display="name"
          :selected="currentlySelectedIntegration ? currentlySelectedIntegration.name : 'Select an Integration'"
          :key="newIntegrationSelectorKey"
          @user_select="setCurrentlySelectedIntegration"
        />
      </div>

      <!-- Date selectors -->
      <div v-if="currentlySelectedIntegration" class="flex space-x-4 items-end">
        <div>
          <label class="block font-medium text-md text-gray-700">Start Date</label>
          <input
            type="date"
            v-model="startDate"
            class="border rounded px-2 py-1"
          />
        </div>
        <div>
          <label class="block font-medium text-md text-gray-700">End Date</label>
          <input
            type="date"
            v-model="endDate"
            class="border rounded px-2 py-1"
          />
        </div>
      </div>

      <!-- Games Selector -->
      <div v-if="currentlySelectedIntegration" class="mt-4">
        <label class="font-medium block">Game(s):</label>
        <DropdownComponent
          :items="all_campaign_games"
          display="name"
          :selected="selectedGameLabel"
          :key="selectedGameLabel"
          @user_select="addGameToFilter"
          class="w-full"
        />
        <div class="flex flex-row flex-wrap gap-1 mt-2">
          <label
            v-for="game in game_filter_array"
            :key="game.uuid"
            class="bg-white rounded-md px-2 py-1 cursor-pointer hover:bg-blue-300"
            @click="removeGameFromFilter(game)"
          >
            {{ game.friendly_name }}
          </label>
        </div>
      </div>

      <!-- Total Engagements Card -->
      <div class="card-container">
        <div class="inner-card">
          <label>Total Engagements:</label>
          <span>{{ totalEngagements }}</span>
        </div>
      </div>

      <!-- Submit button (when not in IntegrationsReporting) -->
      <button
        v-if="currentlySelectedIntegration && currentPanel !== 'IntegrationsReporting'"
        class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded mt-4 col-span-2"
        @click="fetchEngagementsCount"
      >Submit</button>
    </div>

    <!-- Tab Buttons -->
    <TabButtons
      v-if="currentlySelectedIntegration"
      :buttons="panelSwitchingButtonsData"
      :active_button="currentPanel"
      :panel-changed-method="panelChanged"
    />

    <!-- Integrations Reporting Panel -->
    <div v-if="currentlySelectedIntegration && currentPanel === 'IntegrationsReporting'">
      <!-- Country filters + submit -->
      <div class="sm:p-8 p-4 bg-blue-100 rounded-md grid grid-cols-2 gap-4">
        <div class="mt-4">
          <label class="font-medium block">Countries:</label>
          <DropdownComponent
            :items="displayedCountries"
            display="name"
            :selected="selectedCountryLabel"
            :key="selectedCountryLabel"
            @user_select="addCountryToFilter"
            class="w-full"
          />
        </div>
        <div class="flex flex-row flex-wrap gap-1 mt-2">
          <span
            v-for="(code, i) in country_filter_array"
            :key="i"
            class="bg-white inline-flex items-center rounded-md px-2 py-0.5 text-sm cursor-pointer hover:bg-blue-300"
            @click="removeCountryFromFilter(code)"
          >
            {{ countryMapping[code] || code }}
          </span>
        </div>
        <button
          class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded mt-4 w-full col-span-2"
          @click="fetchEngagementsCount"
        >Submit</button>
      </div>

      <LoadingComponent v-if="loading" />

      <!-- Header -->
      <div class="p-4 px-8 bg-white rounded-md text-xl font-medium text-gray-700 text-center flex flex-row items-center lg:justify-between justify-center gap-2 flex-wrap">
        <img src="@/assets/GamefamLogo.png" class="w-40 h-9 object-cover" alt="gamefam-logo" />
        {{ headerName }}
        <p class="text-sm text-gray-500 text-center mt-2" v-if="lastUpdatedAt">
          Last updated at: {{ new Date(lastUpdatedAt).toLocaleString() }}
        </p>
        <span class="text-sm text-gray-500">{{ headerDate }}</span>
      </div>

      <!-- Game Stats -->
      <div v-if="gameStatsTable.length">
        <h2 class="text-xl font-bold mt-4">Game Stats</h2>
        <TableComponent
          :table_data="gameStatsTable"
          :total_column_heading_name="'Game'"
          :exported_file_name="getExportedFileName('GameStats')"
          :show_total_row="false"
          :show_average_row="false"
        />
      </div>

      <!-- Daily Engagement Line Chart -->
      <div class="mt-6">
        <div v-if="dailyEngagementChartOptions.series.length">
          <highcharts :options="dailyEngagementChartOptions" />
        </div>
        <div v-else><p>No daily engagements data available.</p></div>
      </div>

      <!-- Total Engagements By Day -->
      <div v-if="dailyTotals.length">
        <h2 class="text-xl font-bold mt-4">Total Engagements By Day</h2>
        <TableComponent
          :columns="dailyTotalsColumns"
          :table_data="dailyTotals"
          :total_column_heading_name="'Engagements'"
          :show_total_row="false"
          :show_average_row="false"
          :exported_file_name="getExportedFileName('Daily Totals')"
        />
      </div>

      <!-- Daily Totals by Game -->
      <div v-if="dailyTotalsByGame.length">
        <h2 class="text-xl font-bold mt-4">Total Engagements By Game By Day</h2>
        <TableComponent
          :key="dailyTotalsByGameColumns.join('-')"
          :columns="dailyTotalsByGameColumns"
          :table_data="dailyTotalsByGame"
          :total_column_heading_name="'Game'"
          :exported_file_name="getExportedFileName('Daily Totals by Game')"
        />
      </div>

      <!-- Daily Engagements per Event per Game (as tables) -->
      <div v-if="engagementCharts.length">
        <h2 class="text-xl font-bold mt-4">Engagements By Game By Day Deep Dive</h2>
        <div
          v-for="chart in engagementCharts"
          :key="chart.game"
          class="mt-8 bg-white p-4 rounded-md shadow"
        >
          <h3 class="text-lg font-medium mb-2">{{ chart.game }}</h3>
          <TableComponent
            :columns="chart.columns"
            :table_data="chart.data"
            :total_column_heading_name="'Event'"
            :exported_file_name="getExportedFileName(`DailyEngagementsByGame_${chart.game}`)"
          />
        </div>
      </div>
    </div>

    <!-- GameStats Reporting Panel -->
    <keep-alive>
      <div v-show="currentPanel === 'GameStatsReporting'">
        <LegoGameStats
          :games="legoGames"
          :startDate="startDate"
          :endDate="endDate"
          :triggerFetch="triggerLegoFetch"
          @chartsFetched="handleChartsFetched"
          @loadingState="loadingStatuses.loadingGameStats = $event"
        />
      </div>
    </keep-alive>
  </div>
</template>

<script>
import TableComponent from '@/components/TableComponent.vue';
import TabButtons from '@/components/TabButtons.vue';
import LegoGameStats from '../components/LegoGameStats.vue';
import countryMapping from '@/assets/country_code_to_country_name_map.json';
import { Chart } from 'highcharts-vue';

export default {
  name: "LegoCombinedReporting",
  components: {
    TableComponent,
    LegoGameStats,
    TabButtons,
    highcharts: Chart,
  },
  data() {
    return {
      newIntegrations: [],
      currentlySelectedIntegration: null,
      newIntegrationSelectorKey: 0,
      startDate: '',
      endDate: '',
      triggerLegoFetch: 0,
      charts: null,
      engagements: [],
      dailyEngagements: null,
      all_campaign_games: [],
      game_filter_array: [],
      dailyTotals: [],
      dailyTotalsColumns: [],
      dailyTotalsByGame: [],
      dailyTotalsByGameColumns: [],
      engagementCharts: [],
      integrationCountries: [],
      country_filter_array: [],
      engagementsCachedAt: null,
      dailyEngagementsCachedAt: null,
      dailyTotalsCachedAt: null,
      dailyTotalsByGameCachedAt: null,
      engagementChartsCachedAt: null,

      loadingStatuses: {
        loadingIntegrations: false,
        loadingEngagementData: false,
        loadingGameStats: false
      },
      currentPanel: 'IntegrationsReporting',
      panelSwitchingButtonsData: [
        { name: 'IntegrationsReporting', text: 'Integrations Reporting', panelIsActive: true },
        { name: 'GameStatsReporting', text: 'Game Stats Reporting', panelIsActive: true },
      ],
      dailyEngagementChartOptions: {
        chart: { type: 'line', zoomType: 'x' },
        title: { text: 'Daily Engagements' },
        xAxis: { type: 'datetime' },
        yAxis: { title: { text: 'Engagements' } },
        series: []
      }
    };
  },
  computed: {
    lastUpdatedAt() {
      const times = [
        this.engagementsCachedAt,
        this.dailyEngagementsCachedAt,
        this.dailyTotalsCachedAt,
        this.dailyTotalsByGameCachedAt,
        this.engagementChartsCachedAt
      ].filter(Boolean).map(t => new Date(t).getTime());

      if (!times.length) return null;

      const oldest = Math.min(...times);
      return new Date(oldest).toISOString();
    },
    loading() {
      return Object.values(this.loadingStatuses).some(v => v);
    },
    headerName() {
      return this.currentlySelectedIntegration?.name || '';
    },
    headerDate() {
      if (!this.startDate || !this.endDate) return '';
      const fmt = d => new Date(d).toLocaleDateString('en-US');
      return `${fmt(this.startDate)} - ${fmt(this.endDate)}`;
    },
    totalEngagements() {
      return this.engagements
        .reduce((sum, r) => sum + Number(r.Total || 0), 0)
        .toLocaleString();
    },
    selectedGameLabel() {
      return this.game_filter_array.length
        ? this.game_filter_array.map(g => g.friendly_name).join(', ')
        : 'All Games';
    },
    displayedCountries() {
      const pref = [
        'Country - GB','Country - US','Country - RU','Country - AU',
        'Country - BR','Country - CA','Country - DE','Country - ES',
        'Country - FR','Country - IT','Country - MX'
      ];
      const all = this.integrationCountries;
      const preferred = pref.filter(c => all.includes(c)).map(c => ({ code:c, name:countryMapping[c]||c }));
      const others = all.filter(c => !pref.includes(c))
        .map(c => ({ code:c, name:countryMapping[c]||c }))
        .sort((a,b)=>a.name.localeCompare(b.name));
      return [...preferred, ...others];
    },
    selectedCountryLabel() {
      return this.country_filter_array.length
        ? this.country_filter_array.map(c => countryMapping[c]||c).join(', ')
        : 'All Countries';
    },
    legoGames() {
      return this.game_filter_array.length
        ? this.game_filter_array.slice()
        : this.all_campaign_games.slice();
    },
    countryMapping() {
      return countryMapping;
    },
    gameStatsTable() {
      // guard clause: nothing to build
      if (!this.charts?.visits?.length) return []

      const { visits, ccus, dailyActiveUsers: dau, monthlyActiveUsers: mau, sessionLength: sess } = this.charts

      // 1) build lookup maps
      const makeMap = arr =>
        new Map(arr.map(({ game, response }) => [game.universe_id, response]))
      const [visitsMap, ccusMap, dauMap, mauMap, sessMap] =
        [visits, ccus, dau, mau, sess].map(makeMap)

      // 2) avg helper
      const computeAvg = dataObj => {
        const vals = Object.values(dataObj || {}).map(v => Number(v) || 0)
        return vals.length
          ? Math.round(vals.reduce((sum, x) => sum + x, 0) / vals.length)
          : 0
      }

      // 3) dailyTotalsByGame lookup
      const totalsByGameMap =
        new Map(this.dailyTotalsByGame.map(row => [row.Game, row]))

      // 4) build table
      return visits.map(({ game: { universe_id: id, friendly_name: name } }) => {
        const visitsData  = visitsMap.get(id)?.data?.[0]?.data  || {}
        const totalVisits = Object.values(visitsData).reduce((s, v) => s + Number(v || 0), 0)

        const engagementsRow   = totalsByGameMap.get(name) || {}
        const totalEngagements = Object.entries(engagementsRow)
          .reduce((sum, [key, val]) => key === 'Game' ? sum : sum + Number(val || 0), 0)

        const ccuSeries = ccusMap.get(id)?.data || []
        const avgCCU    = computeAvg(ccuSeries.find(s => s.name === 'CCU Avg')?.data)
        const peakCCU   = Math.max(
          0,
          ...(ccuSeries.find(s => s.name === 'CCU Min/Max')?.data || [])
            .map(a => Number(a[2] || 0))
        )
        

        return {
          Game:               name,
          Visits:             Math.round(totalVisits),
          'Total Engagements': totalEngagements,
          'Avg. CCU':         avgCCU,
          'Peak CCU':         peakCCU,
          'Avg. DAU':         computeAvg(dauMap .get(id)?.data?.[0]?.data),
          MAU:                computeAvg(mauMap .get(id)?.data?.[0]?.data),
          'Session Length':   computeAvg(sessMap.get(id)?.data?.[0]?.data),
        }
      })
    }
  },
  created() {
    this.fetchNewIntegrations();
  },
  methods: {
    async fetchNewIntegrations() {
      this.loadingStatuses.loadingIntegrations = true;
      try {
        const { data } = await this.$http.get('/new-integrations');
        this.newIntegrations = data.data;
      } finally {
        this.loadingStatuses.loadingIntegrations = false;
        this.triggerLegoFetch++
      }
    },
    setCurrentlySelectedIntegration(sel) {
      this.currentlySelectedIntegration = sel;
      this.startDate = sel.start_date;
      this.endDate = sel.end_date;
      this.game_filter_array = [];
      this.country_filter_array = [];
      this.fetchIntegrationGames();
      this.fetchIntegrationCountries();
      this.fetchEngagementsCount();
    },
    async fetchIntegrationGames() {
      if (!this.currentlySelectedIntegration) return;
      const { data } = await this.$http.get(
        `/new-integrations/${this.currentlySelectedIntegration.uuid}/games`
      );
      this.all_campaign_games = data;
      this.triggerLegoFetch++
    },
    async fetchIntegrationCountries() {
      if (!this.currentlySelectedIntegration) return;
      const { data } = await this.$http.get(
        `/new-integrations/${this.currentlySelectedIntegration.uuid}/countries`
      );
      this.integrationCountries = data;
    },
    async fetchEngagementsCount() {
      this.triggerLegoFetch++
      if (!this.currentlySelectedIntegration || !this.startDate || !this.endDate) return;
      this.loadingStatuses.loadingEngagementData = true;

      const params = {
        integrationCodesCsv: this.currentlySelectedIntegration.integration_codes_csv,
        disallowedEventNamesCsv: this.currentlySelectedIntegration.disallowed_event_names_csv,
        start_date: this.startDate,
        end_date: this.endDate,
      };
      if (this.game_filter_array.length) {
        params.game_universe_ids = this.game_filter_array.map(g => g.universe_id);
      }
      if (this.country_filter_array.length) {
        params.country_codes = [...new Set(this.country_filter_array)];
      }

      try {
        const [
          engR,
          dailyR,
          totalsR,
          byGameR,
          chartsByGameR
        ] = await Promise.all([
          this.$http.get('/new-intgration-reporting/engagements-count',     { params }),
          this.$http.get('/new-intgration-reporting/daily-engagements',     { params }),
          this.$http.get('/new-intgration-reporting/daily-totals',          { params }),
          this.$http.get('/new-intgration-reporting/daily-totals-by-game',  { params }),
          this.$http.get('/new-intgration-reporting/engagement-charts-by-game', { params }),
        ]);

        this.engagements = engR.data.data;
        this.engagementsCachedAt = engR.data.cached_at;

        this.dailyEngagements = dailyR.data.data;
        this.dailyEngagementsCachedAt = dailyR.data.cached_at;

        this.dailyTotalsColumns = totalsR.data.data.columns;
        this.dailyTotals = totalsR.data.data.data.map(row =>
          Object.fromEntries(totalsR.data.data.columns.map((c, i) => [c, row[i]]))
        );
        this.dailyTotalsCachedAt = totalsR.data.cached_at;

        this.dailyTotalsByGameColumns = byGameR.data.data.columns;
        this.dailyTotalsByGame = byGameR.data.data.data;
        this.dailyTotalsByGameCachedAt = byGameR.data.cached_at;

        this.engagementCharts = chartsByGameR.data.data;
        this.engagementChartsCachedAt = chartsByGameR.data.cached_at;

        this.renderDailyEngagementChart();
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingStatuses.loadingEngagementData = false;
      }
    },
    handleChartsFetched(charts) {
      this.charts = charts;
    },

    renderDailyEngagementChart() {
      const rows = this.dailyEngagements?.data;
      const cols = this.dailyEngagements?.columns;
      if (!rows?.length || !cols?.length) {
        this.dailyEngagementChartOptions.series = [];
        return;
      }
      const dateCol = cols[0];
      const valueCols = cols.slice(1);
      const dataPoints = rows.map(r => [
        new Date(r[dateCol]).getTime(),
        valueCols.reduce((sum, c) => sum + Number(r[c] || 0), 0)
      ]).sort((a, b) => a[0] - b[0]);
      this.dailyEngagementChartOptions.series = [{ name: 'Daily Engagements', data: dataPoints }];
    },
    addGameToFilter(game) {
      if (!this.game_filter_array.some(g => g.uuid === game.uuid)) {
        this.game_filter_array.push(game);
      }
    },
    removeGameFromFilter(game) {
      this.game_filter_array = this.game_filter_array.filter(g => g.uuid !== game.uuid);
    },
    addCountryToFilter(sel) {
      if (sel.code && !this.country_filter_array.includes(sel.code)) {
        this.country_filter_array.push(sel.code);
      }
    },
    removeCountryFromFilter(code) {
      this.country_filter_array = this.country_filter_array.filter(c => c !== code);
    },
    panelChanged(name) {
      this.currentPanel = name;
    },
    getExportedFileName(title) {
      const ig = this.currentlySelectedIntegration?.name || 'NoIntegration';
      const gs = this.game_filter_array.length
        ? this.game_filter_array.map(g => g.friendly_name).join('-')
        : 'AllGames';
      const cs = this.country_filter_array.length
        ? this.country_filter_array.map(c => countryMapping[c]||c).join('-')
        : 'AllCountries';
      const sd = this.startDate.replace(/-/g, '') || 'NoStart';
      const ed = this.endDate.replace(/-/g, '')   || 'NoEnd';
      return `${title}_${ig}_${gs}_${cs}_${sd}_${ed}`;
    }
  }
};
</script>

<style lang="postcss" scoped>
.inner-card {
  @apply flex flex-col gap-2 items-center;
}
.card-container span {
  @apply text-3xl font-bold;
}
.card-container {
  @apply bg-white rounded-md flex flex-col items-center justify-center p-4;
}
</style>
